import React from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Chip, IconButton, Tooltip, Link,
    Paper
} from '@mui/material';
import { PrimaryButton } from './Buttons';
import { formatStatusLabel } from '../../constants/statusColors';
import {
    Description as DocumentIcon, Download as DownloadIcon, Visibility as ViewIcon,
    Person as PersonIcon, Build as EquipmentIcon, Inventory as ConsumableIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';

const DocumentDisplay = ({
    title,
    documents = [], // Changed from credentials to documents for consistency
    entityType = 'general', // 'technician', 'equipment', 'consumable', 'general'
    showMetadata = true,
    maxHeight = '400px',
    entityData = null, // Additional entity information (name, quantity, etc.)
    onDelete = null, // Callback function for delete operations
    canDelete = false, // Whether delete operations are allowed
    showDeleteButton = false, // Whether to show delete button
    showUploadButton = false, // Whether to show upload button
    onUploadClick = null, // Callback function for upload button clicks
    onRefresh = null // Callback function to refresh data
}) => {
    const getEntityIcon = (type) => {
        switch (type) {
            case 'technician':
                return <PersonIcon color="primary" />;
            case 'equipment':
                return <EquipmentIcon color="primary" />;
            case 'consumable':
                return <ConsumableIcon color="primary" />;
            default:
                return <DocumentIcon color="primary" />;
        }
    };

    const getFileTypeIcon = (filename) => {
        const extension = filename.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'pdf':
                return <Chip label="PDF" size="small" color="error" variant="outlined" />;
            case 'doc':
            case 'docx':
                return <Chip label="DOC" size="small" color="primary" variant="outlined" />;
            case 'xls':
            case 'xlsx':
                return <Chip label="XLS" size="small" color="success" variant="outlined" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return <Chip label="IMG" size="small" color="info" variant="outlined" />;
            default:
                return <Chip label={extension?.toUpperCase() || 'FILE'} size="small" color="default" variant="outlined" />;
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDelete = (documentId, filename) => {
        if (onDelete && canDelete) {
            onDelete(documentId, filename);
        }
    };

    return (
        <Paper variant="outlined" sx={{ borderRadius: 1 }}>
            {/* Header */}
            <Box sx={{ 
                p: 2, 
                backgroundColor: 'grey.100', 
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {getEntityIcon(entityType)}
                        <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                            {title}
                        </Typography>
                        {/* Document count chip moved to left side for consistency with equipment assignments */}
                        <Chip 
                            label={`${documents.length} document${documents.length !== 1 ? 's' : ''}`} 
                            size="small" 
                            variant="outlined"
                            color="primary"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {showUploadButton && onUploadClick && (
                            <PrimaryButton
                                size="small"
                                startIcon={<DocumentIcon />}
                                onClick={onUploadClick}
                            >
                                Upload document
                            </PrimaryButton>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Content - Side by Side Layout */}
            <Box sx={{ display: 'flex', minHeight: '200px' }}>
                {/* Left Side - Entity Metadata */}
                {entityData && (
                    <Box sx={{ 
                        width: '280px', 
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'grey.50'
                    }}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'medium', color: 'text.primary' }}>
                                {entityType === 'technician' ? 'Technician Details' :
                                 entityType === 'equipment' ? 'Equipment Details' :
                                 entityType === 'consumable' ? 'Consumable Details' : 'Entity Details'}
                            </Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {entityData.name && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Name:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.name}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.serial_no && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Serial No:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.serial_no}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.batch_no && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Batch No:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.batch_no}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.quantity && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Quantity:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.quantity}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.device_type && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Device Type:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.device_type}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.device_model && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Device Model:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.device_model}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.status && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Status:</Typography>
                                        <Chip size="small" label={formatStatusLabel(entityData.status)} color="primary" />
                                    </Box>
                                )}
                                {entityData.unit && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Unit:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.unit}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.location && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Location:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.location}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.email && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Email:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.email}
                                        </Typography>
                                    </Box>
                                )}
                                {entityData.phone && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">Phone:</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium', textAlign: 'right', maxWidth: '60%' }}>
                                            {entityData.phone}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* Right Side - Documents Grid */}
                <Box sx={{ flex: 1, p: 2 }}>
                    {documents.length > 0 ? (
                        <Grid container spacing={2}>
                            {documents.map((document) => (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={document.id}>
                                    <Card 
                                        variant="outlined" 
                                        sx={{ 
                                            height: '100%',
                                            '&:hover': { 
                                                borderColor: 'primary.main',
                                                backgroundColor: 'grey.50'
                                            },
                                            transition: 'all 0.15s ease-in-out'
                                        }}
                                    >
                                        <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            {/* File Header */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                <DocumentIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        fontWeight: 'medium',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        flex: 1
                                                    }}
                                                    title={document.original_filename || document.filename}
                                                >
                                                    {document.original_filename || document.filename}
                                                </Typography>
                                            </Box>

                                            {/* File Type Badge */}
                                            <Box sx={{ mb: 1.5 }}>
                                                {getFileTypeIcon(document.original_filename || document.filename)}
                                            </Box>

                                            {/* File Details - Fixed height to ensure consistent layout */}
                                            <Box sx={{ mb: 2, minHeight: '80px' }}>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    Type: {document.document_type_name || document.document_type?.name || document.document_type || 'Unknown'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    Size: {formatFileSize(document.file_size)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                    Uploaded: {formatDate(document.uploaded_at || document.created_at)}
                                                </Typography>
                                                {document.uploaded_by && (
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                        By: {document.uploaded_by}
                                                    </Typography>
                                                )}
                                                {/* Add empty space if no uploaded_by to maintain consistent height */}
                                                {!document.uploaded_by && (
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                        &nbsp;
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Action Buttons - Always at the bottom */}
                                            <Box sx={{
                                                display: 'flex',
                                                gap: 0.5,
                                                justifyContent: 'center',
                                                flexWrap: 'wrap',
                                                mt: 'auto' // Push to bottom
                                            }}>
                                                {document.presigned_url && (
                                                    <Chip
                                                        icon={<ViewIcon sx={{ fontSize: 16 }} />}
                                                        label="View"
                                                        size="small"
                                                        component={Link}
                                                        href={document.presigned_url}
                                                        target="_blank"
                                                        clickable
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                )}
                                                {document.download_url && (
                                                    <Chip
                                                        icon={<DownloadIcon sx={{ fontSize: 16 }} />}
                                                        label="Download"
                                                        size="small"
                                                        component={Link}
                                                        href={document.download_url}
                                                        download
                                                        clickable
                                                        color="success"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                )}
                                                {/* Delete Button - Only show when allowed */}
                                                {showDeleteButton && canDelete && onDelete && (
                                                    <Chip
                                                        icon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                                        label="Delete"
                                                        size="small"
                                                        onClick={() => handleDelete(document.id, document.original_filename || document.filename)}
                                                        clickable
                                                        color="error"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ 
                            textAlign: 'center', 
                            py: 4,
                            color: 'text.secondary'
                        }}>
                            <DocumentIcon sx={{ fontSize: 40, mb: 2, opacity: 0.5 }} />
                            <Typography variant="body1">
                                No documents available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};

export default DocumentDisplay; 